<!-- eslint-disable vue/no-v-html -->
<template>
  <div id="support">
    <h3>Support</h3>
    <div class="form" :class="{ loading: isLoadingForm }">
      <textarea v-model="messageText" spellcheck="true" :disabled="isSupportBlocked" placeholder="Type a question or message to a travel expert..." @focus="authorize($event.target)" @input="textareaResize" />
      <div class="checkboxlabel" :class="{ on: messageIsUrgent === true }" @click="messageIsUrgent = !messageIsUrgent">
        <div class="checkbox" />
        <label>I have a travel emergency and need help</label>
      </div>
      <div v-if="isSupportBlocked" class="support-blocked">
        Support is available 3 days before trip start on {{ supportAvailableFromFormatted }}
      </div>
      <button v-else class="block" @click="save">Send Message</button>
    </div>
    <div v-if="!isSupportBlocked" class="list" :class="{ loading: isLoading }">
      <div v-for="m in messagesFiltered" :key="m.id" class="message" :class="{ expert: m.is_expert, customer: !m.is_expert, demo: props.tripId == 164480 }">
        <div class="info">
          <div class="author">{{ m.author }}</div>
          <div class="timing">{{ utilDurationMessage(m.created) }}</div>
        </div>
        <div class="inner">
          <div v-if="m.is_expert" class="icon fox">&#xff;</div>
          <div v-if="m.is_urgent" class="urgent">Urgent Message</div>
          <div class="text" @click="click" v-html="text(m.text)" />
        </div>
      </div>
      <div v-show="isMore" class="more">
        <a @click="more">View All</a>
      </div>
      <div v-if="messages.length == 0" class="none">Your request is processing. We will be in touch shortly.</div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, inject } from "vue";
import useApi from "../../utils/apiCalls";
import DateTime from "../../utils/datetime";
import TextareaUtils from "../../utils/textarea";

// Props and emits
const props = defineProps(["isReloadMessages", "tripId", "trip"]);
const emit = defineEmits(["error", "scroll", "update:isReloadMessages", "update:itineraryIndex"]);

const { utilDurationMessage, getDateFlightfoxFull } = DateTime();
const { textareaResize } = TextareaUtils();

// Inject dependencies
const userIsExpert = inject("userIsExpert");

// States
const isLoading = ref(true);
const isLoadingForm = ref(false);
const messages = ref([]);
const messageIsUrgent = ref(false);
const messagePageMax = ref(3);
const messageText = ref("");

// API utility
const { apiGetRequest, apiPostRequest } = useApi(emit);

// Computed properties
const isSupportBlocked = computed(() => {
  if (!props.trip?.is_support_only || !props.trip?.support_available_from) return false;
  return new Date() < new Date(props.trip.support_available_from);
});
const supportAvailableFromFormatted = computed(() => {
  if (!props.trip?.support_available_from) return "";
  var d = new Date(props.trip.support_available_from);
  return getDateFlightfoxFull(d) + " at " + d.toLocaleTimeString("en", { hour: "numeric", minute: "2-digit" });
});
const isMore = computed(() => messages.value.length > messagePageMax.value);
const messagesFiltered = computed(() => {
  const len = messages.value.length < messagePageMax.value ? messages.value.length : messagePageMax.value;
  return messages.value.slice(0, len);
});

// Watchers
watch(
  () => props.isReloadMessages,
  val => {
    if (val === true) {
      loadMessages();
      emit("update:isReloadMessages", false);
    }
  }
);

// Lifecycle hooks
onMounted(() => {
  loadMessages();
});

// Methods
const authorize = el => {
  if (userIsExpert.value) {
    alert("Experts cannot post a message using the Customer UI.");
    el.blur();
  }
};

const click = e => {
  if (e.target.matches(".itinerary-link")) {
    const index = e.target.dataset.index;
    clickItinerary(index);
  }
};

const clickItinerary = index => {
  emit("update:itineraryIndex", index);
  emit("scroll");
};

const loadMessages = () => {
  const path = `/trips/${props.tripId}/messages`;

  apiGetRequest({
    path,
    onSuccess: data => {
      messages.value = data;
      isLoading.value = false;
      isLoadingForm.value = false;
    },
    onError: error => {
      isLoading.value = false;
      isLoadingForm.value = false;
      emit("error", error);
    },
  });
};

const more = () => {
  messagePageMax.value = messages.value.length;
};

const save = () => {
  if (!messageText.value.trim()) {
    return;
  }

  isLoadingForm.value = true;
  const path = `/trips/${props.tripId}/messages`;

  apiPostRequest({
    path,
    data: {
      text: messageText.value,
      is_urgent: messageIsUrgent.value,
    },
    onSuccess: () => {
      messageText.value = "";
      messageIsUrgent.value = false;
    },
    onError: error => {
      emit("error", error);
    },
  });
};

const text = text => {
  let enhanced = text;

  // URL links
  enhanced = enhanced.replace(/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9:%_+.~#?&/=@,!;()]*)/gi, textUrl);

  // Itinerary links
  enhanced = enhanced.replace(/[^&]#[0-9]+[^;]/gi, textItinerary);

  // expert.flightfox.com/trips => app.flightfox.com/trips
  enhanced = enhanced.replace(/expert\.flightfox\.com\/trips/gi, "app.flightfox.com/trips");

  return enhanced;
};

const textItinerary = match => {
  const index = match.replace(/[^0-9]/g, "");
  return match.replace(`#${index}`, `<a class="itinerary-link copy-paste-allow" data-index="${index}">#${index}</a>`);
};

const textUrl = match => {
  const visibleText = match.length > 75 ? match.slice(0, 75) + "..." : match;
  return `<a href="${match}" target="_blank">${visibleText}</a>`;
};
</script>

<style lang="less">
#support {
  .support-blocked {
    color: @secondary;
    padding: 1rem 0;
  }

  .form {
    textarea {
      margin-bottom: 1rem;
      padding-right: 3rem;
      resize: none;
    }

    .checkboxlabel {
      margin: 1rem 0 2rem;

      &:hover {
        label {
          color: @dark;
        }

        .checkbox:before {
          background: @secondary;
        }
      }

      &.on {
        label {
          color: @dark;
        }

        .checkbox {
          background: @secondary;
          border-color: @secondary;
        }
      }
    }
  }

  .list {
    .message {
      margin-top: 2rem;
      padding-left: 1rem;

      .inner {
        border-radius: 1rem;
        padding: 2rem;
        position: relative;
      }

      .fox {
        background: @secondary;
        border-radius: 100%;
        color: @white;
        height: 2rem;
        left: -1rem;
        line-height: 2rem;
        position: absolute;
        text-align: center;
        top: 2rem;
        width: 2rem;
      }

      .info {
        color: @mid;
        display: flex;
        margin-bottom: 0.5rem;
      }

      .author {
        flex: 1;
        margin-right: 0.5rem;
        text-align: right;
      }

      .timing {
        color: @placeholder;
      }

      .urgent {
        color: @secondary;
        margin-bottom: 1rem;
      }

      .text {
        line-height: 1.75;
        overflow: hidden;
        white-space: pre-line;
      }

      &.customer {
        .inner {
          border: 0.0625rem solid @border;
        }
      }

      &.expert {
        .inner {
          background: @white;
          border: 0.0625rem solid @border;
          box-shadow: 0.5rem 0.5rem 0.5rem rgba(0, 0, 0, 0.05);
        }
      }

      @keyframes pulse-amber {
        0% {
          background-color: #fff4cc;
        }
        50% {
          background-color: #ffffff;
        }
        100% {
          background-color: #fff4cc;
        }
      }

      &:first-child.demo .inner {
        animation: pulse-amber 1s ease-in-out 2;
      }
    }

    .more {
      padding-top: 1rem;
      text-align: center;
    }

    .none {
      color: @secondary;
      padding: 2rem;
      text-align: center;
    }
  }
}
</style>
